import React, { useState, useEffect } from 'react';
import { Save, Eye, EyeOff, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/api/apiClient';
import toast from '../../utils/toast';
import { z } from 'zod';
import { PasskeyManagement } from './PasskeyManagement';
import { ProfileSection } from './ProfileSection';
import { TWInput } from '../ui/TWInput';
import { TWSelect } from '../ui/TWSelect';
import clsx from 'clsx';

// Separate Zod Schemas pro Sektion
const personalDataSchema = z.object({
  firstName: z.string()
    .min(2, 'Vorname muss mindestens 2 Zeichen lang sein')
    .max(50, 'Vorname darf maximal 50 Zeichen lang sein')
    .regex(/^[a-zA-ZäöüÄÖÜß\s-]+$/, 'Vorname darf nur Buchstaben, Leerzeichen und Bindestriche enthalten'),
  lastName: z.string()
    .min(2, 'Nachname muss mindestens 2 Zeichen lang sein')
    .max(50, 'Nachname darf maximal 50 Zeichen lang sein')
    .regex(/^[a-zA-ZäöüÄÖÜß\s-]+$/, 'Nachname darf nur Buchstaben, Leerzeichen und Bindestriche enthalten'),
  email: z.string()
    .optional()
    .or(z.literal(''))
    .refine((email) => {
      if (!email || email === '') return true;
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email);
    }, 'Ungültige E-Mail-Adresse'),
  username: z.string()
    .min(3, 'Benutzername muss mindestens 3 Zeichen lang sein')
    .max(50, 'Benutzername darf maximal 50 Zeichen lang sein')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Benutzername darf nur Buchstaben, Zahlen, Unterstriche und Bindestriche enthalten')
    .optional()
    .or(z.literal('')),
  companyName: z.string()
    .min(2, 'Firmenname muss mindestens 2 Zeichen lang sein')
    .max(100, 'Firmenname darf maximal 100 Zeichen lang sein')
    .optional()
    .or(z.literal(''))
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Aktuelles Passwort erforderlich'),
  newPassword: z.string()
    .min(8, 'Passwort muss mindestens 8 Zeichen lang sein')
    .max(128, 'Passwort darf maximal 128 Zeichen lang sein')
    .regex(/[A-Z]/, 'Passwort muss mindestens einen Großbuchstaben enthalten')
    .regex(/[a-z]/, 'Passwort muss mindestens einen Kleinbuchstaben enthalten')
    .regex(/[0-9]/, 'Passwort muss mindestens eine Zahl enthalten')
    .regex(/[^A-Za-z0-9]/, 'Passwort muss mindestens ein Sonderzeichen enthalten'),
  confirmPassword: z.string()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwörter stimmen nicht überein',
  path: ['confirmPassword']
});

const addressSchema = z.object({
  street: z.string()
    .refine((val) => val === '' || val.length <= 100, { message: 'Straßenname darf maximal 100 Zeichen lang sein' })
    .refine((val) => val === '' || /^[a-zA-ZäöüÄÖÜß0-9\s.-]+$/.test(val), { message: 'Straße darf nur Buchstaben, Zahlen, Leerzeichen, Punkte und Bindestriche enthalten' })
    .optional()
    .or(z.literal('')),
  houseNumber: z.string()
    .refine((val) => val === '' || val.length <= 10, { message: 'Hausnummer darf maximal 10 Zeichen lang sein' })
    .refine((val) => val === '' || /^[0-9a-zA-Z\s/-]+$/.test(val), { message: 'Hausnummer darf nur Zahlen, Buchstaben und Bindestriche enthalten' })
    .optional()
    .or(z.literal('')),
  zipCode: z.string()
    .refine((val) => val === '' || /^\d{5}$/.test(val), { message: 'PLZ muss genau 5 Ziffern enthalten (z.B. 12345)' })
    .optional()
    .or(z.literal('')),
  city: z.string()
    .refine((val) => val === '' || val.length <= 100, { message: 'Stadt darf maximal 100 Zeichen lang sein' })
    .refine((val) => val === '' || /^[a-zA-ZäöüÄÖÜß\s.-]+$/.test(val), { message: 'Stadt darf nur Buchstaben, Leerzeichen, Punkte und Bindestriche enthalten' })
    .optional()
    .or(z.literal('')),
  country: z.enum(['DE', 'AT', 'CH'], { errorMap: () => ({ message: 'Bitte ein gültiges Land wählen' }) })
});

// Normalisiert Ländercodes aus der DB (Altdaten-Kompatibilität)
const normalizeCountry = (country: string | undefined): 'DE' | 'AT' | 'CH' => {
  if (!country) return 'DE';
  const map: Record<string, 'DE' | 'AT' | 'CH'> = {
    'DE': 'DE', 'AT': 'AT', 'CH': 'CH',
    'ÖS': 'AT', 'SC': 'CH',
    'Deutschland': 'DE', 'Österreich': 'AT', 'Schweiz': 'CH'
  };
  return map[country] || 'DE';
};

const sanitizeInput = (input: string): string => {
  return input.trim().replace(/[<>]/g, '').replace(/javascript:/gi, '').replace(/on\w+=/gi, '');
};

type SavingSection = 'personal' | 'password' | 'address' | 'company' | null;

const countryOptions = [
  { value: 'DE', label: 'Deutschland' },
  { value: 'AT', label: 'Österreich' },
  { value: 'CH', label: 'Schweiz' },
];

// ============================================
// Save Button Komponente
// ============================================
interface SaveButtonProps {
  onClick: () => void;
  loading: boolean;
  disabled: boolean;
  label?: string;
}

const SaveButton: React.FC<SaveButtonProps> = ({ onClick, loading, disabled, label = 'Speichern' }) => (
  <div className="flex justify-end pt-4 mt-2">
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200',
        'bg-brand text-gray-950 shadow-sm',
        'hover:bg-brand-hover hover:shadow-md',
        'focus:outline-none focus:ring-2 focus:ring-brand/40 focus:ring-offset-2',
        'focus:ring-offset-white dark:focus:ring-offset-gray-950',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none'
      )}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Speichern...</span>
        </>
      ) : (
        <>
          <Save className="w-4 h-4" />
          <span>{label}</span>
        </>
      )}
    </button>
  </div>
);

// ============================================
// Passkey Section Komponente
// ============================================
const PasskeySection: React.FC = () => {
  const [headerAction, setHeaderAction] = useState<React.ReactNode>(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6 py-8 md:py-12">
      <div>
        <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
          Passkeys
        </h2>
        <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
          Passwortloser Login mit biometrischer Authentifizierung
        </p>
        {headerAction && (
          <div className="mt-4">
            {headerAction}
          </div>
        )}
      </div>
      <div className="md:col-span-2" data-sensitive>
        <PasskeyManagement onRenderHeaderAction={setHeaderAction} />
      </div>
    </div>
  );
};

interface ProfileEditFormProps {
  user: any;
  onUpdate: () => void;
}

export const ProfileEditForm: React.FC<ProfileEditFormProps> = ({ user, onUpdate }) => {
  const [savingSection, setSavingSection] = useState<SavingSection>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    accountType: 'private',
    companyName: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    street: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    country: 'DE'
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        username: user.username || '',
        accountType: user.accountType || 'private',
        companyName: user.companyName || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        street: user.address?.street || '',
        houseNumber: user.address?.houseNumber || '',
        zipCode: user.address?.zipCode || '',
        city: user.address?.city || '',
        country: normalizeCountry(user.address?.country)
      });
      setValidationErrors({});
    }
  }, [user]);

  // Calculate password strength
  useEffect(() => {
    const password = formData.newPassword;
    if (!password) {
      setPasswordStrength(0);
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength += 10;
    if (password.length >= 12) strength += 10;
    if (password.length >= 16) strength += 10;
    if (/[a-z]/.test(password)) strength += 10;
    if (/[A-Z]/.test(password)) strength += 10;
    if (/[0-9]/.test(password)) strength += 10;
    if (/[^A-Za-z0-9]/.test(password)) strength += 10;

    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const varietyCount = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

    if (varietyCount >= 3) strength += 10;
    if (varietyCount === 4) strength += 5;

    if (/(?:0123|1234|2345|3456|4567|5678|6789|7890)/.test(password)) strength -= 20;
    if (/(.)\1{2,}/.test(password)) strength -= 15;
    if (/^[0-9]+[a-zA-Z]+$/.test(password) || /^[a-zA-Z]+[0-9]+$/.test(password)) strength -= 10;

    const weakPasswords = ['password', 'test', 'admin', '1234', 'qwerty', 'abc'];
    if (weakPasswords.some(weak => password.toLowerCase().includes(weak))) strength -= 15;

    strength = Math.max(0, Math.min(100, strength));
    setPasswordStrength(strength);
  }, [formData.newPassword]);

  // Änderungs-Detection pro Sektion
  const hasPersonalDataChanges = (): boolean => {
    return formData.firstName !== (user?.firstName || '') ||
           formData.lastName !== (user?.lastName || '') ||
           formData.email !== (user?.email || '') ||
           formData.username !== (user?.username || '');
  };

  const hasPasswordChanges = (): boolean => {
    return formData.newPassword !== '' || formData.currentPassword !== '' || formData.confirmPassword !== '';
  };

  const hasAddressChanges = (): boolean => {
    return formData.street !== (user?.address?.street || '') ||
           formData.houseNumber !== (user?.address?.houseNumber || '') ||
           formData.zipCode !== (user?.address?.zipCode || '') ||
           formData.city !== (user?.address?.city || '') ||
           formData.country !== normalizeCountry(user?.address?.country);
  };

  const hasCompanyDataChanges = (): boolean => {
    return formData.companyName !== (user?.companyName || '') ||
           hasAddressChanges();
  };

  const handleInputChange = (field: string, value: string) => {
    const sanitized = sanitizeInput(value);
    setFormData(prev => ({ ...prev, [field]: sanitized }));
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  // Save Personal Data
  const handleSavePersonalData = async () => {
    try {
      setSavingSection('personal');
      setValidationErrors({});

      const data = {
        firstName: sanitizeInput(formData.firstName),
        lastName: sanitizeInput(formData.lastName),
        email: formData.email ? sanitizeInput(formData.email.toLowerCase()) : '',
        username: sanitizeInput(formData.username),
      };

      try {
        personalDataSchema.parse({ ...data, companyName: '' });
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errors: Record<string, string> = {};
          error.errors.forEach((err) => {
            const pathItem = err.path[0];
            if (pathItem !== undefined) {
              errors[pathItem.toString()] = err.message;
            }
          });
          setValidationErrors(errors);
          toast.error('Bitte überprüfen Sie Ihre Eingaben');
          return;
        }
      }

      const payload: Record<string, any> = {
        firstName: data.firstName,
        lastName: data.lastName,
      };
      if (data.email) payload.email = data.email;
      if (data.username) {
        payload.username = data.username;
      } else if (user?.username && !data.username) {
        payload.username = null;
      }

      const response = await apiClient.put('/auth/user/profile', payload);

      if (response.data?.emailChangeRequested) {
        toast.success('Bestätigungslink wurde an die neue E-Mail-Adresse gesendet');
        // E-Mail-Feld auf alten Wert zurücksetzen (noch nicht geändert)
        setFormData(prev => ({ ...prev, email: user?.email || '' }));
      } else {
        toast.success('Persönliche Daten gespeichert');
      }
      onUpdate();
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('Diese E-Mail-Adresse oder dieser Benutzername wird bereits verwendet');
      } else {
        toast.error(error.response?.data?.error || 'Fehler beim Speichern');
      }
    } finally {
      setSavingSection(null);
    }
  };

  // Save Password
  const handleSavePassword = async () => {
    try {
      setSavingSection('password');
      setValidationErrors({});

      const data = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      };

      try {
        passwordSchema.parse(data);
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errors: Record<string, string> = {};
          error.errors.forEach((err) => {
            const pathItem = err.path[0];
            if (pathItem !== undefined) {
              errors[pathItem.toString()] = err.message;
            }
          });
          setValidationErrors(errors);
          toast.error('Bitte überprüfen Sie Ihre Eingaben');
          return;
        }
      }

      await apiClient.put('/auth/user/profile', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });

      // Reset password fields after success
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

      toast.success('Passwort erfolgreich geändert');
      onUpdate();
    } catch (error: any) {
      if (error.response?.status === 401) {
        setValidationErrors({ currentPassword: 'Das aktuelle Passwort ist nicht korrekt' });
        toast.error('Das aktuelle Passwort ist nicht korrekt');
      } else {
        toast.error(error.response?.data?.error || 'Fehler beim Ändern des Passworts');
      }
    } finally {
      setSavingSection(null);
    }
  };

  // Save Address
  const handleSaveAddress = async () => {
    try {
      setSavingSection('address');
      setValidationErrors({});

      const data = {
        street: sanitizeInput(formData.street),
        houseNumber: sanitizeInput(formData.houseNumber),
        zipCode: sanitizeInput(formData.zipCode),
        city: sanitizeInput(formData.city),
        country: sanitizeInput(formData.country)
      };

      try {
        addressSchema.parse(data);
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errors: Record<string, string> = {};
          error.errors.forEach((err) => {
            const pathItem = err.path[0];
            if (pathItem !== undefined) {
              errors[pathItem.toString()] = err.message;
            }
          });
          setValidationErrors(errors);
          toast.error('Bitte überprüfen Sie Ihre Eingaben');
          return;
        }
      }

      await apiClient.put('/auth/user/profile', {
        address: {
          street: data.street || null,
          houseNumber: data.houseNumber || null,
          zipCode: data.zipCode || null,
          city: data.city || null,
          country: data.country
        }
      });

      toast.success('Adresse gespeichert');
      onUpdate();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSavingSection(null);
    }
  };

  // Save Company Data (Firmenname + Adresse)
  const handleSaveCompanyData = async () => {
    try {
      setSavingSection('company');
      setValidationErrors({});

      const addressData = {
        street: sanitizeInput(formData.street),
        houseNumber: sanitizeInput(formData.houseNumber),
        zipCode: sanitizeInput(formData.zipCode),
        city: sanitizeInput(formData.city),
        country: sanitizeInput(formData.country)
      };

      const companyName = sanitizeInput(formData.companyName);

      try {
        addressSchema.parse(addressData);
      } catch (error) {
        if (error instanceof z.ZodError) {
          const errors: Record<string, string> = {};
          error.errors.forEach((err) => {
            const pathItem = err.path[0];
            if (pathItem !== undefined) {
              errors[pathItem.toString()] = err.message;
            }
          });
          setValidationErrors(errors);
          toast.error('Bitte überprüfen Sie Ihre Eingaben');
          return;
        }
      }

      if (companyName && companyName.length < 2) {
        setValidationErrors({ companyName: 'Firmenname muss mindestens 2 Zeichen lang sein' });
        toast.error('Bitte überprüfen Sie Ihre Eingaben');
        return;
      }

      const payload: Record<string, any> = {
        address: {
          street: addressData.street || null,
          houseNumber: addressData.houseNumber || null,
          zipCode: addressData.zipCode || null,
          city: addressData.city || null,
          country: addressData.country
        }
      };

      if (companyName) {
        payload.companyName = companyName;
      } else if (user?.companyName && !companyName) {
        payload.companyName = null;
      }

      await apiClient.put('/auth/user/profile', payload);

      toast.success('Firmendaten gespeichert');
      onUpdate();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSavingSection(null);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return '#ef4444'; // red-500
    if (passwordStrength < 70) return '#f97316'; // orange-500
    return '#22c55e'; // green-500
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 40) return 'Schwach';
    if (passwordStrength < 70) return 'Mittel';
    return 'Stark';
  };

  const isAnySaving = savingSection !== null;

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <>
      {/* Persönliche Daten */}
      <ProfileSection
        title="Persönliche Daten"
        description="Deine grundlegenden Kontoinformationen"
      >
        <div className="space-y-4" data-sensitive>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TWInput
              label="Vorname *"
              value={formData.firstName}
              onChange={(v) => handleInputChange('firstName', v)}
              placeholder="Max"
              error={validationErrors.firstName}
            />
            <TWInput
              label="Nachname *"
              value={formData.lastName}
              onChange={(v) => handleInputChange('lastName', v)}
              placeholder="Mustermann"
              error={validationErrors.lastName}
            />
          </div>

          <TWInput
            label="E-Mail-Adresse"
            type="email"
            value={formData.email}
            onChange={(v) => handleInputChange('email', v)}
            placeholder="max@beispiel.de"
            error={validationErrors.email}
          />

          <TWInput
            label="Benutzername"
            value={formData.username}
            onChange={(v) => handleInputChange('username', v)}
            placeholder="maxmustermann"
            error={validationErrors.username}
          />

          {hasPersonalDataChanges() && (
            <SaveButton
              onClick={handleSavePersonalData}
              loading={savingSection === 'personal'}
              disabled={isAnySaving}
            />
          )}
        </div>
      </ProfileSection>

      {/* Firmendaten (nur Firmenkonten) */}
      {formData.accountType === 'company' && (
        <ProfileSection
          title="Firmendaten"
          description="Angaben zu deinem Unternehmen und Firmenadresse"
        >
          <div className="space-y-4" data-sensitive>
            <TWInput
              label="Firmenname"
              value={formData.companyName}
              onChange={(v) => handleInputChange('companyName', v)}
              placeholder="Musterfirma GmbH"
              error={validationErrors.companyName}
            />

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-4">
              <TWInput
                label="Straße"
                value={formData.street}
                onChange={(v) => handleInputChange('street', v)}
                placeholder="Musterstraße"
                error={validationErrors.street}
              />
              <TWInput
                label="Nr."
                value={formData.houseNumber}
                onChange={(v) => handleInputChange('houseNumber', v)}
                placeholder="123"
                error={validationErrors.houseNumber}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
              <TWInput
                label="PLZ"
                value={formData.zipCode}
                onChange={(v) => handleInputChange('zipCode', v)}
                placeholder="12345"
                maxLength={5}
                error={validationErrors.zipCode}
              />
              <TWInput
                label="Stadt"
                value={formData.city}
                onChange={(v) => handleInputChange('city', v)}
                placeholder="Berlin"
                error={validationErrors.city}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Land
              </label>
              <TWSelect
                value={formData.country}
                onChange={(v) => handleInputChange('country', v)}
                options={countryOptions}
                className="w-full"
              />
            </div>

            {hasCompanyDataChanges() && (
              <SaveButton
                onClick={handleSaveCompanyData}
                loading={savingSection === 'company'}
                disabled={isAnySaving}
              />
            )}
          </div>
        </ProfileSection>
      )}

      {/* Passwort ändern */}
      <ProfileSection
        title="Passwort ändern"
        description="Aktualisiere dein Passwort für mehr Sicherheit. Lasse die Felder leer, wenn du dein Passwort nicht ändern möchtest."
      >
        <div className="space-y-4">
          <TWInput
            label="Aktuelles Passwort"
            type={showPassword ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={(v) => handleInputChange('currentPassword', v)}
            placeholder="Ihr aktuelles Passwort"
            error={validationErrors.currentPassword}
            rightElement={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
          />

          <div className="space-y-3">
            <TWInput
              label="Neues Passwort"
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(v) => handleInputChange('newPassword', v)}
              placeholder="Neues Passwort"
              error={validationErrors.newPassword}
              rightElement={
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
            />

            {formData.newPassword && (
              <div className="space-y-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    Passwortstärke: <span className={clsx(
                      'font-medium',
                      passwordStrength < 40 && 'text-status-error',
                      passwordStrength >= 40 && passwordStrength < 70 && 'text-orange-500',
                      passwordStrength >= 70 && 'text-status-success'
                    )}>{getPasswordStrengthText()}</span>
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 text-xs">{passwordStrength}%</span>
                </div>

                <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${passwordStrength}%`,
                      backgroundColor: getPasswordStrengthColor()
                    }}
                  />
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {[
                    { check: formData.newPassword.length >= 8, label: 'Min. 8 Zeichen' },
                    { check: /[A-Z]/.test(formData.newPassword), label: 'Großbuchstabe' },
                    { check: /[a-z]/.test(formData.newPassword), label: 'Kleinbuchstabe' },
                    { check: /[0-9]/.test(formData.newPassword), label: 'Zahl' },
                    { check: /[^A-Za-z0-9]/.test(formData.newPassword), label: 'Sonderzeichen' }
                  ].map((req) => (
                    <span
                      key={req.label}
                      className={clsx(
                        'text-xs px-2.5 py-1 rounded-md transition-colors',
                        req.check
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                      )}
                    >
                      {req.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <TWInput
            label="Neues Passwort bestätigen"
            type="password"
            value={formData.confirmPassword}
            onChange={(v) => handleInputChange('confirmPassword', v)}
            placeholder="Passwort wiederholen"
            error={validationErrors.confirmPassword}
          />

          {hasPasswordChanges() && (
            <SaveButton
              onClick={handleSavePassword}
              loading={savingSection === 'password'}
              disabled={isAnySaving}
              label="Passwort ändern"
            />
          )}
        </div>
      </ProfileSection>

      {/* Adresse (nur Privatkonten) */}
      {formData.accountType !== 'company' && (
        <ProfileSection
          title="Adresse"
          description="Deine Rechnungs- und Lieferadresse"
        >
          <div className="space-y-4" data-sensitive>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-4">
              <TWInput
                label="Straße"
                value={formData.street}
                onChange={(v) => handleInputChange('street', v)}
                placeholder="Musterstraße"
                error={validationErrors.street}
              />
              <TWInput
                label="Nr."
                value={formData.houseNumber}
                onChange={(v) => handleInputChange('houseNumber', v)}
                placeholder="123"
                error={validationErrors.houseNumber}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
              <TWInput
                label="PLZ"
                value={formData.zipCode}
                onChange={(v) => handleInputChange('zipCode', v)}
                placeholder="12345"
                maxLength={5}
                error={validationErrors.zipCode}
              />
              <TWInput
                label="Stadt"
                value={formData.city}
                onChange={(v) => handleInputChange('city', v)}
                placeholder="Berlin"
                error={validationErrors.city}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Land
              </label>
              <TWSelect
                value={formData.country}
                onChange={(v) => handleInputChange('country', v)}
                options={countryOptions}
                className="w-full"
              />
            </div>

            {hasAddressChanges() && (
              <SaveButton
                onClick={handleSaveAddress}
                loading={savingSection === 'address'}
                disabled={isAnySaving}
              />
            )}
          </div>
        </ProfileSection>
      )}

      {/* Passkeys */}
      <PasskeySection />

      {/* Account-Löschung wird in einer separaten Komponente (AccountDeleteSection)
          ganz unten auf der Profilseite gerendert, damit sie nicht zwischen
          den Edit-Sektionen liegt. */}
    </>
  );
};
